import React, { useState, useCallback, useEffect } from 'react';
import { Box, IconButton, Tooltip, Theme } from '@mui/material';
import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import { TopNav, SideMenu } from '../navigation';
import { SIDE_MENU_SECTIONS } from '../navigation/sideMenuSections';
import OrgSwitchToast from '../navigation/OrgSwitchToast';
import Enforce2FAGate from '../security/Enforce2FAGate';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTrackRecentlyVisited } from '../../hooks/useRecentlyVisited';
import { useRoleLanding } from '../../hooks/useRoleLanding';
import { http } from '../../api/client';
import { LicensedProductsProvider } from '../../auth/ProductBoundary';
import { UNRESOLVED_LICENSE, type ProductLicense } from '../../auth/productAccess';
import { GUTTER, HEADER_HEIGHT, railWidth } from '../../theme/shellMetrics';

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 1280);
  const [activeSection, setActiveSection] = useState('Dashboard');
  // The licence starts UNKNOWN, not optimistic.
  //
  // This was seeded `['qutime', 'pureoa']` — every product, before asking. So a
  // PureOA-only tenant was told it held QuTime for the ~200ms until
  // /v1/me/dashboard answered, and every QuTime-only capability flashed in the
  // navigation. Face capture is the case that makes this more than cosmetic: a
  // PureOA org captures no biometrics, and the flash offered its admins a face
  // enrolment roster and a face review queue that the backend then 403s.
  //
  // `resolved` is carried EXPLICITLY rather than inferred from `[]`, because an
  // empty array is a real answer the server can give (a lapsed subscription) and
  // conflating it with "not asked yet" is what would make a spinner never end.
  const [license, setLicense] = useState<ProductLicense>(UNRESOLVED_LICENSE);
  const location = useLocation();
  const navigate = useNavigate();
  const { landingPath } = useRoleLanding();

  // Persist each navigation to the RecentlyVisited widget's store.
  useTrackRecentlyVisited();

  // Fetch org licensed_products once on mount.
  //
  // A failure also RESOLVES, to an empty licence. Leaving it unresolved would
  // park the gated routes on a spinner forever; resolving it empty denies them,
  // which is the same answer the backend would give and is recoverable by a
  // reload. Fail-closed either way — the pattern is session-discipline §12's
  // "what happens when the narrowing input cannot be resolved at all".
  useEffect(() => {
    let alive = true;
    http.get('/v1/me/dashboard').then((res: any) => {
      if (!alive) return;
      setLicense({ products: res?.licensed_products ?? [], resolved: true });
    }).catch(() => {
      if (alive) setLicense({ products: [], resolved: true });
    });
    return () => { alive = false; };
  }, []);

  // Sync active section with current path and handle navigation
  useEffect(() => {
    const path = location.pathname.split('/')[1];
    if (path) {
      const section = path.charAt(0).toUpperCase() + path.slice(1);
      setActiveSection(section || 'Dashboard');
    } else {
      // No path → role-correct landing. ``useRoleLanding`` is the
      // single source of truth (NEXT_SESSION P3 governance debt).
      navigate(landingPath);
    }
  }, [location.pathname, navigate, landingPath]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);
  
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    // Navigate to the corresponding route when section changes
    navigate(`/${section.toLowerCase()}`);
  };

  // Show the rail only for sections that actually have a menu. This was
  // `activeSection !== 'Dashboard'` — a blacklist of one — and `activeSection`
  // is just the capitalised first path segment, so `/account/security` resolved
  // to 'Account', passed the test, and rendered an empty 240px column with only
  // the collapse chevron in it. A whitelist means an unknown section gets no
  // rail instead of an empty one.
  const showSidebar = SIDE_MENU_SECTIONS.has(activeSection);

  // Styles for the toggle button
  const toggleButtonStyle = {
    position: 'fixed',
    // Sits on the rail's edge, overlapping it by half the chevron's width. Derived,
    // because these used to be 56 / 232 — a second copy of the rail widths that a
    // change to the rail would silently leave behind.
    insetInlineStart: railWidth(collapsed) - 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 20,
    height: 48,
    borderRadius: '0 8px 8px 0',
    backgroundColor: 'background.paper',
    zIndex: (theme: Theme) => theme.zIndex.drawer + 2,
    transition: (theme: Theme) => theme.transitions.create(['left'], {
      duration: '0.2s',
      easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)'
    }),
    '&:hover': {
      backgroundColor: 'grey.100',
      transform: 'translateY(-50%) scale(1.05)',
      '& .MuiSvgIcon-root': {
        color: 'primary.main',
      }
    },
    '&:active': {
      transform: 'translateY(-50%)',
      transition: 'none',
    },
    '&:focus-visible': {
      outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
      backgroundColor: 'background.paper',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      insetInlineStart: -1,
      width: 1,
      height: '100%',
      backgroundColor: 'divider',
    }
  };

  return (
    <LicensedProductsProvider value={license}>
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* `undefined` while the licence is unknown, which is TopNav's existing
          "do not gate" value — deliberately not `[]`. The module tabs are
          cosmetic and every module route is enforced server-side, so an empty
          primary navigation on first paint would look broken for no safety gain.
          The face capability is the opposite trade and is gated fail-closed:
          it prompts for biometric data, so a flash there is not cosmetic. */}
      <TopNav activeSection={activeSection} onSectionChange={handleSectionChange} licensedProducts={license.resolved ? license.products : undefined} />
      {/* Privacy audit Wave 5 polish 2026-05-22 — post-org-switch toast.
          Reads sessionStorage SWITCH_TOAST_KEY set by OrgSwitcher.handlePick
          before reload. Auto-clears on first render. */}
      <OrgSwitchToast />
      
      <Box sx={{ 
        display: 'flex', 
        pt: `${HEADER_HEIGHT}px`,
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        // One canvas, whatever the route. This used to be
        // `!showSidebar ? 'grey.50' : 'background.paper'`, so /dashboard was grey and
        // /organization white — decided by whether a MENU exists, which is not a
        // reason for a page to change colour (owner review 2026-08-01).
        bgcolor: 'grey.50',
      }}>
        {showSidebar && (
          <SideMenu 
            collapsed={collapsed} 
            activeSection={activeSection}
            licensedProducts={license.products}
            productsResolved={license.resolved}
          />
        )}
        
        {showSidebar && (
          <Tooltip title={collapsed ? "Expand Menu" : "Collapse Menu"} placement="right">
            <IconButton
              onClick={toggleCollapsed}
              size="small"
              aria-label={collapsed ? "Expand sidebar menu" : "Collapse sidebar menu"}
              aria-expanded={!collapsed}
              aria-controls="side-menu"
              disableRipple={true}
              sx={toggleButtonStyle}
            >
              {collapsed ? (
                <ArrowRight
                  fontSize="small"
                  sx={{
                    transform: 'translateX(-2px)',
                    color: 'text.secondary',
                  }}
                />
              ) : (
                <ArrowLeft
                  fontSize="small"
                  sx={{
                    transform: 'translateX(-2px)',
                    color: 'text.secondary',
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
        )}
        
        <Box 
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            // THE page gutter, once. It used to be `px: '42px'` repeated in 92 page
            // components, so a new page inherited nothing and every change was a
            // sweep. Pages now supply only their vertical rhythm.
            px: `${GUTTER}px`,
            transition: (theme: Theme) => theme.transitions.create(
              ['margin', 'width'],
              {
                duration: '0.2s',
                easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)'
              }
            ),
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <Enforce2FAGate />
    </Box>
    </LicensedProductsProvider>
  );
};

export default MainLayout;